import { toast } from 'sonner'
import { useCallback, useMemo, useRef, useState } from 'react'
import { useSnapshot } from 'valtio'
import { useTodoMeta } from './hooks/use-todo-meta'
import { createSubtask, actions, store } from '@/store'
import { buildBackendUrl } from '@/lib/backend-url'
import {
  buildInfoTabs,
  getInitialInfoTabValue,
  toIndexedEntries,
} from './todo-item-info-tabs.utils'
import { TodoItemInfoTabs } from './todo-item-info-tabs'
import { useTaskAutocomplete } from './hooks/use-task-autocomplete'

export function TodoItemInfoGrid({
  todo,
  enabledFeatures,
  onUpdate,
  subtaskText,
  onSubtaskTextChange,
  viewMode = 'list',
}) {
  const snap = useSnapshot(store)
  const isBoard = viewMode === 'board'
  const fileInputRef = useRef(null)

  const { completion: subtaskCompletion, acceptCompletion: acceptSubtaskCompletion } =
    useTaskAutocomplete(subtaskText, true, { type: 'subtask', parentId: todo.id })

  const tabs = useMemo(() => buildInfoTabs(enabledFeatures, todo), [enabledFeatures, todo])
  const reminderEntries = useMemo(() => toIndexedEntries(todo.reminders), [todo.reminders])
  const attachmentEntries = useMemo(() => {
    const taskAttachments = snap.attachments.filter((a) => a.task_id === todo.id)
    return toIndexedEntries(taskAttachments)
  }, [snap.attachments, todo.id])

  const [activeTab, setActiveTab] = useState('')

  const handleSurfaceInteraction = useCallback((e) => {
    e.stopPropagation()
  }, [])

  const handleTabChange = useCallback((value) => {
    setActiveTab(value)
  }, [])

  const {
    handleStartDateChange,
    handleEndDateChange,
    handleRecurrenceChange,
    handleToggleAssignee,
    handleAddLocation,
    handleRemoveLocation,
    handleUpdateLocation,
  } = useTodoMeta(todo, onUpdate)

  const handleRemoveReminder = useCallback(
    (e) => {
      const index = Number(e.currentTarget.dataset.index)
      if (Number.isNaN(index)) {
        return
      }

      const reminders = [...(todo.reminders || [])]
      reminders.splice(index, 1)
      onUpdate(todo.id, {
        reminders,
      })
    },
    [onUpdate, todo.id, todo.reminders],
  )

  const handleAddReminder = useCallback(
    (date, method) => {
      actions.addReminder(todo.id, date, method)
    },
    [todo.id],
  )

  const handleUploadClick = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  const handleRemoveAttachment = useCallback((e) => {
    const id = e.currentTarget.dataset.id
    if (!id) return
    actions.removeAttachment(id)
    toast.success('Attachment removed')
  }, [])

  const handleAttachmentChange = useCallback(
    async (e) => {
      const files = Array.from(e.target.files || [])
      if (files.length === 0) return

      toast.promise(actions.attachFiles(todo.id, files), {
        loading: 'Uploading files...',
        success: (results) => `Uploaded ${results.length} file${results.length > 1 ? 's' : ''}`,
        error: 'Upload failed',
      })

      e.currentTarget.value = ''
    },
    [todo.id],
  )

  const handleDownloadAttachment = useCallback((id, filename) => {
    const url = buildBackendUrl(`/api/attachments/${id}/download`)
    window.open(url, '_blank')
    toast.success(`Downloading ${filename}`)
  }, [])

  const handleDownloadAll = useCallback(() => {
    attachmentEntries.forEach((entry) => {
      handleDownloadAttachment(entry.value.id, entry.value.file_name)
    })
  }, [attachmentEntries, handleDownloadAttachment])

  const handleAddSubtask = useCallback(() => {
    const trimmedSubtaskText = subtaskText.trim()
    if (!trimmedSubtaskText) {
      return
    }

    onUpdate(todo.id, {
      subtasks: [...(todo.subtasks || []), createSubtask(trimmedSubtaskText)],
    })
    onSubtaskTextChange('')
  }, [onSubtaskTextChange, onUpdate, subtaskText, todo.id, todo.subtasks])

  const handleSubtaskTextInputChange = useCallback(
    (e) => {
      onSubtaskTextChange(e.target.value)
    },
    [onSubtaskTextChange],
  )

  const handleSubtaskInputKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        handleAddSubtask()
      }
    },
    [handleAddSubtask],
  )

  const handleToggleSubtask = useCallback(
    (subtaskId) => {
      const updatedSubtasks = (todo.subtasks || []).map((subtask) =>
        subtask.id === subtaskId
          ? {
              ...subtask,
              completed: !subtask.completed,
            }
          : subtask,
      )

      onUpdate(todo.id, {
        subtasks: updatedSubtasks,
      })
    },
    [onUpdate, todo.id, todo.subtasks],
  )

  const handleToggleSubtaskClick = useCallback(
    (e) => {
      e.stopPropagation()
      const { subtaskId } = e.currentTarget.dataset
      if (subtaskId) {
        handleToggleSubtask(subtaskId)
      }
    },
    [handleToggleSubtask],
  )

  const handleToggleSubtaskKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        e.stopPropagation()
        const { subtaskId } = e.currentTarget.dataset
        if (subtaskId) {
          handleToggleSubtask(subtaskId)
        }
      }
    },
    [handleToggleSubtask],
  )

  const handleDeleteSubtaskClick = useCallback(
    (e) => {
      e.stopPropagation()
      const { subtaskId } = e.currentTarget.dataset
      if (!subtaskId) {
        return
      }

      const updatedSubtasks = (todo.subtasks || []).filter((subtask) => subtask.id !== subtaskId)
      onUpdate(todo.id, {
        subtasks: updatedSubtasks,
      })
    },
    [onUpdate, todo.id, todo.subtasks],
  )

  if (tabs.length === 0) {
    return null
  }

  const activeTabIsAvailable = tabs.some((tab) => tab.value === activeTab)
  const resolvedActiveTab = activeTabIsAvailable ? activeTab : getInitialInfoTabValue(tabs)

  return (
    <TodoItemInfoTabs
      todo={todo}
      tabs={tabs}
      activeTab={resolvedActiveTab}
      isBoard={isBoard}
      onTabChange={handleTabChange}
      onSurfaceInteraction={handleSurfaceInteraction}
      reminderEntries={reminderEntries}
      attachmentEntries={attachmentEntries}
      handleStartDateChange={handleStartDateChange}
      handleEndDateChange={handleEndDateChange}
      handleRecurrenceChange={handleRecurrenceChange}
      handleToggleAssignee={handleToggleAssignee}
      handleAddLocation={handleAddLocation}
      handleRemoveLocation={handleRemoveLocation}
      handleUpdateLocation={handleUpdateLocation}
      handleAddReminder={handleAddReminder}
      handleUploadClick={handleUploadClick}
      handleRemoveReminder={handleRemoveReminder}
      handleRemoveAttachment={handleRemoveAttachment}
      handleAttachmentChange={handleAttachmentChange}
      handleDownloadAttachment={handleDownloadAttachment}
      handleDownloadAll={handleDownloadAll}
      subtaskText={subtaskText}
      subtaskCompletion={subtaskCompletion}
      acceptSubtaskCompletion={acceptSubtaskCompletion}
      handleAddSubtask={handleAddSubtask}
      handleSubtaskTextInputChange={handleSubtaskTextInputChange}
      handleSubtaskInputKeyDown={handleSubtaskInputKeyDown}
      handleToggleSubtaskClick={handleToggleSubtaskClick}
      handleToggleSubtaskKeyDown={handleToggleSubtaskKeyDown}
      handleDeleteSubtaskClick={handleDeleteSubtaskClick}
      fileInputRef={fileInputRef}
    />
  )
}
